import React from 'react';
import { ExternalLink } from 'lucide-react';

type MetadataRecord = Record<string, any> | null | undefined;

/**
 * AR-210 (TC-34.5): KB and scrape summaries arrive with inline markdown
 * ('**Summary:** ... **Highlights:**') and were printed with the asterisks
 * (Modes + Slash Commands: '/kb must not leak raw markup tokens'). Render
 * **bold** / __bold__ as <strong> and drop stray markers; the surrounding
 * <p>/<li> stay exactly as they were, so only the markup changes.
 */
function renderInlineMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
  return parts.map((part, i) => {
    const bold = part.match(/^(?:\*\*([^*]+)\*\*|__([^_]+)__)$/);
    if (bold) return <strong key={i}>{bold[1] ?? bold[2]}</strong>;
    return <React.Fragment key={i}>{part.replace(/\*\*/g, '')}</React.Fragment>;
  });
}

type SlashCommandBlocksProps = {
  metadata?: MetadataRecord;
};

export const SlashCommandBlocks: React.FC<SlashCommandBlocksProps> = ({
  metadata,
}) => {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }

  const blocks: React.ReactNode[] = [];

  if (
    metadata.crmCommand &&
    Array.isArray(metadata.crmResults) &&
    metadata.crmResults.length
  ) {
    blocks.push(
      <CrmResultsBlock
        key="crm"
        headline={metadata.crmHeadline as string | undefined}
        results={metadata.crmResults as Array<Record<string, any>>}
      />,
    );
  }

  if (
    metadata.kbCommand &&
    (metadata.kbSummary || metadata.kbHighlights?.length)
  ) {
    blocks.push(
      <KbSummaryBlock
        key="kb"
        summary={metadata.kbSummary as string | undefined}
        highlights={Array.isArray(metadata.kbHighlights) ? metadata.kbHighlights : []}
        sources={Array.isArray(metadata.kbSources) ? metadata.kbSources : []}
      />,
    );
  }

  if (
    metadata.webCommand &&
    Array.isArray(metadata.webResults) &&
    metadata.webResults.length
  ) {
    blocks.push(
      <WebResultsBlock
        key="web"
        results={metadata.webResults as Array<Record<string, any>>}
      />,
    );
  }

  if (
    metadata.webContentCommand &&
    (metadata.webContentSummary || metadata.webContentHighlights?.length)
  ) {
    blocks.push(
      <WebContentBlock
        key="scrape"
        title={metadata.title as string | undefined}
        summary={metadata.webContentSummary as string | undefined}
        highlights={
          Array.isArray(metadata.webContentHighlights)
            ? metadata.webContentHighlights
            : []
        }
      />,
    );
  }

  if (!blocks.length) {
    return null;
  }

  return <div className="mt-4 space-y-4">{blocks}</div>;
};

const CrmResultsBlock: React.FC<{
  headline?: string;
  results: Array<Record<string, any>>;
}> = ({ headline, results }) => {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 text-sm font-semibold text-slate-800">
        {headline ?? 'CRM results'}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {results.map((result, idx) => (
          <div
            key={`${result.id ?? idx}`}
            className="rounded-md border border-slate-200 bg-white p-3 shadow-xs"
          >
            <div className="text-sm font-medium text-slate-900">
              {result.name ?? 'Unknown'}
            </div>
            {result.email && (
              <div className="text-sm text-slate-500">{result.email}</div>
            )}
            {result.phone && (
              <div className="text-sm text-slate-500">{result.phone}</div>
            )}
            {result.stage && (
              <div className="text-sm text-amber-600">Stage: {result.stage}</div>
            )}
            {result.city && (
              <div className="text-sm text-slate-500">{result.city}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const KbSummaryBlock: React.FC<{
  summary?: string;
  highlights: string[];
  sources: Array<{ title?: string; url?: string; domain?: string }>;
}> = ({ summary, highlights, sources }) => {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="mb-2 text-sm font-semibold text-amber-900">
        Knowledge base summary
      </div>
      {summary && (
        <p className="text-sm text-amber-900/90">{renderInlineMarkdown(summary)}</p>
      )}
      {highlights.length > 0 && (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-amber-900/90">
          {highlights.map((item, idx) => (
            <li key={`${item}-${idx}`}>{renderInlineMarkdown(item)}</li>
          ))}
        </ul>
      )}
      {sources.length > 0 && (
        <div className="mt-3 text-xs text-amber-900/70">
          <div className="font-semibold">Sources</div>
          <ul className="mt-1 space-y-1">
            {sources.map((source, idx) => (
              <li key={`${source.title ?? source.url ?? idx}`}>
                {source.url ? (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-amber-900 underline decoration-dotted"
                  >
                    {source.title ?? source.url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  source.title ?? 'Reference'
                )}
                {source.domain && <span className="ml-2 text-amber-700">({source.domain})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const WebResultsBlock: React.FC<{
  results: Array<Record<string, any>>;
}> = ({ results }) => {
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
      <div className="mb-2 text-sm font-semibold text-sky-900">
        Web search results
      </div>
      <div className="space-y-3">
        {results.map((result, idx) => (
          <div key={`${result.url ?? idx}`} className="text-sm text-sky-900/90">
            <div className="font-medium">
              {result.url ? (
                <a
                  href={result.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sky-900 underline decoration-dotted"
                >
                  {result.title ?? result.url}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                result.title ?? `Result ${idx + 1}`
              )}
            </div>
            {result.domain && (
              <div className="text-sm text-sky-700">{result.domain}</div>
            )}
            {result.snippet && (
              <div className="mt-1 text-sm text-sky-900/80">{result.snippet}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const WebContentBlock: React.FC<{
  title?: string | null;
  summary?: string;
  highlights: string[];
}> = ({ title, summary, highlights }) => {
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <div className="text-sm font-semibold text-emerald-900">
        {title ?? 'Page summary'}
      </div>
      {summary && (
        <p className="mt-2 text-sm text-emerald-900/90">{renderInlineMarkdown(summary)}</p>
      )}
      {highlights.length > 0 && (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-emerald-900/90">
          {highlights.map((item, idx) => (
            <li key={`${item}-${idx}`}>{renderInlineMarkdown(item)}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

